import React, { useState, useEffect } from 'react';
import './App.css';
import axios from 'axios';

import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Separator } from './components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Alert, AlertDescription } from './components/ui/alert';
import { 
  Leaf, Shield, Wallet, Heart, Menu, X, ShoppingBag, Watch, Star, Sparkles, ArrowRight, 
  User, LogOut, Package, Car, Shirt, Zap, Gift, CreditCard, CheckCircle, ExternalLink, 
  Phone, Lock, Eye, EyeOff, Settings, Globe, Upload, Image as ImageIcon, Trash2, Plus 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Home Page Component
const HomePage = ({ user, formatCurrency, goToShop, goToSellerRegistration, setShowSignupModal }) => {
  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6 sm:mb-8">
            <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-black/5 rounded-full text-xs sm:text-sm text-gray-600 mb-6 sm:mb-8">
              <Leaf className="w-3 sm:w-4 h-3 sm:h-4 mr-2 text-green-600" />
              Circular Luxury • Sustainable Fashion
            </div>
          </div>
          
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tight text-black mb-6 sm:mb-8 leading-none">
            PERFECT
            <br />
            <span className="font-serif italic">Circle</span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-gray-500 mb-8 sm:mb-12 max-w-2xl mx-auto font-light leading-relaxed px-4 sm:px-0">
            Authenticated pre-owned designer pieces that reduce environmental impact. 
            <br className="hidden sm:block" />
            <span className="text-gray-700 font-medium">Every purchase rewards your sustainable choices.</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-12 sm:mb-16 px-4 sm:px-0">
            <Button 
              size="lg" 
              onClick={() => {
                if (user) {
                  goToShop();
                } else {
                  setShowSignupModal(true);
                }
              }} 
              className="w-full sm:w-auto bg-black hover:bg-gray-800 text-white px-8 sm:px-12 py-3 sm:py-4 text-xs sm:text-sm tracking-[0.2em] uppercase font-light border-0 rounded-none h-12 sm:h-14"
            >
              Explore Collection
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              onClick={goToSellerRegistration} 
              className="w-full sm:w-auto border-black text-black hover:bg-black hover:text-white px-8 sm:px-12 py-3 sm:py-4 text-xs sm:text-sm tracking-[0.2em] uppercase font-light rounded-none h-12 sm:h-14"
            >
              Consign Your Luxury
            </Button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-8 md:gap-16 text-center px-4 sm:px-0">
            <div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-light text-black mb-1 sm:mb-2">50K+</div>
              <p className="text-xs tracking-wide uppercase text-gray-500">Litres Water Saved</p>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-light text-black mb-1 sm:mb-2">800+</div>
              <p className="text-xs tracking-wide uppercase text-gray-500">CO₂ Reduced (L)</p>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-light text-black mb-1 sm:mb-2">{formatCurrency(12000, user?.currency || 'GBP')}</div>
              <p className="text-xs tracking-wide uppercase text-gray-500">Credits Earned</p>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-5 sm:w-6 h-8 sm:h-10 border-2 border-gray-300 rounded-full flex justify-center">
            <div className="w-1 h-2 sm:h-3 bg-gray-400 rounded-full mt-1 sm:mt-2"></div>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 sm:py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-black mb-4 sm:mb-6 tracking-tight">
              Curated by Category
            </h2>
            <p className="text-gray-500 text-base sm:text-lg font-light max-w-2xl mx-auto px-4 sm:px-0">
              Discover luxury pieces organized by style, each authenticated by our experts
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {[
              { 
                name: 'Women\'s', 
                category: 'women', 
                image: 'https://images.unsplash.com/photo-1590739169125-a9438401596a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmYXNoaW9ufGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc1NDA2NDUzN3ww&ixlib=rb-4.1.0&q=85',
                description: 'Handbags, accessories & timepieces'
              },
              { 
                name: 'Men\'s', 
                category: 'men', 
                image: 'https://images.unsplash.com/photo-1647954864643-53af66068f61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxkZXNpZ25lciUyMGJhZ3N8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzU0MDY0NTQ2fDA&ixlib=rb-4.1.0&q=85',
                description: 'Watches, wallets & accessories'
              },
              { 
                name: 'Kids\'', 
                category: 'kids', 
                image: 'https://images.pexels.com/photos/336372/pexels-photo-336372.jpeg',
                description: 'Designer pieces for little ones'
              },
              { 
                name: 'Unisex', 
                category: 'unisex', 
                image: 'https://images.unsplash.com/photo-1590739169125-a9438401596a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmYXNoaW9ufGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc1NDA2NDUzN3ww&ixlib=rb-4.1.0&q=85',
                description: 'Timeless designs for everyone'
              }
            ].map((cat) => (
              <div 
                key={cat.category} 
                className="group cursor-pointer" 
                onClick={() => {
                  if (user) {
                    goToShop(cat.category);
                  } else {
                    setShowSignupModal(true);
                  }
                }}
              >
                <div className="relative overflow-hidden bg-gray-100 aspect-[2/3] mb-3 sm:mb-4 md:mb-6 rounded-sm">
                  <img 
                    src={cat.image} 
                    alt={cat.name}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 filter grayscale contrast-110 ${
                      !user ? 'blur-md' : ''
                    }`}
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-all duration-500"></div>
                  
                  {/* Lock Overlay for Non-logged-in Users */}
                  {!user && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm mx-auto mb-2">
                          <Lock className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-white">
                          <p className="font-medium text-xs mb-2">Locked</p>
                          <Button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowSignupModal(true);
                            }}
                            size="sm"
                            className="bg-white text-black hover:bg-gray-100 text-xs px-2 py-1"
                          >
                            Register
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-center px-2 sm:px-0">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-light text-black mb-1 sm:mb-2 tracking-wide">{cat.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 tracking-wide leading-relaxed">{cat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Impact */}
      <section className="py-16 sm:py-24 md:py-32 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-12 sm:mb-16 md:mb-20">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-light text-black mb-4 sm:mb-6 tracking-tight">Environmental Impact</h3>
            <p className="text-gray-500 text-base sm:text-lg font-light max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              Every pre-owned piece contributes to a more sustainable future. 
              Our circular approach reduces waste and rewards conscious consumption.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12 md:gap-16">
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <div className="text-blue-600 text-2xl sm:text-3xl">💧</div>
              </div>
              <div className="text-3xl sm:text-4xl font-light text-black mb-2 sm:mb-4">50,000+</div>
              <p className="text-gray-600 font-light tracking-wide text-sm sm:text-base">Litres of Water Conserved</p>
            </div>
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <div className="text-green-600 text-2xl sm:text-3xl">🌱</div>
              </div>
              <div className="text-3xl sm:text-4xl font-light text-black mb-2 sm:mb-4">800+</div>
              <p className="text-gray-600 font-light tracking-wide text-sm sm:text-base">Litres CO₂ Emissions Reduced</p>
            </div>
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <div className="text-purple-600 text-2xl sm:text-3xl">💎</div>
              </div>
              <div className="text-3xl sm:text-4xl font-light text-black mb-2 sm:mb-4">{formatCurrency(12000, user?.currency || 'GBP')}+</div>
              <p className="text-gray-600 font-light tracking-wide text-sm sm:text-base">In Wallet Credits Earned</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Shop Page Component  
const ShopPage = ({ 
  products, 
  shopCategory, 
  selectedCategory, 
  user, 
  formatCurrency, 
  handleCategoryChange, 
  handlePurchase, 
  isProcessingPurchase,
  addToWishlist,
  isInWishlist,
  setShopCategory,
  addToCart,
  setShowSignupModal
}) => {
  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Shop Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-serif text-black mb-6">
            {shopCategory === 'all' ? 'All Collections' : 
             shopCategory === 'women' ? 'Women\'s Luxury' :
             shopCategory === 'men' ? 'Men\'s Luxury' :
             shopCategory === 'kids' ? 'Kids\' Luxury' :
             'Unisex Luxury'}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Each piece is authenticated by our experts and comes with detailed environmental impact data.
            {user && (
              <span className="block mt-2 text-sm">
                Prices shown in {user.currency}
              </span>
            )}
          </p>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex justify-center mb-8">
          <div className="flex space-x-2 bg-white rounded-lg p-2">
            {['all', 'women', 'men', 'kids', 'unisex'].map((category) => (
              <Button
                key={category}
                variant={shopCategory === category ? "default" : "ghost"}
                size="sm"
                className={`px-8 py-2 text-sm tracking-wide uppercase font-medium rounded-md ${
                  shopCategory === category 
                    ? 'bg-black text-white hover:bg-gray-900' 
                    : 'text-gray-700 hover:text-black hover:bg-gray-100'
                }`}
                onClick={() => setShopCategory(category)}
              >
                {category === 'all' ? 'All Items' : category.charAt(0).toUpperCase() + category.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Product Type Filter */}
        <div className="flex justify-center mb-16">
          <div className="flex space-x-2">
            {['all', 'bags', 'watches', 'accessories'].map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "ghost"}
                size="sm"
                className={`px-8 py-2 text-sm tracking-wide uppercase font-medium ${
                  selectedCategory === category 
                    ? 'bg-black text-white hover:bg-gray-900' 
                    : 'text-gray-700 hover:text-black hover:bg-white'
                }`}
                onClick={() => handleCategoryChange(category)}
              >
                {category === 'all' ? 'All Types' : category.charAt(0).toUpperCase() + category.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <Card key={product.id} className="group bg-white border-0 shadow-none hover:shadow-lg transition-all duration-300">
              <div className="relative overflow-hidden bg-gray-100 aspect-[2/3]">
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                    !user ? 'blur-md' : ''
                  }`}
                />
                
                {/* Lock Overlay for Non-logged-in Users */}
                {!user && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm mx-auto mb-2">
                        <Lock className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-white text-center">
                        <p className="font-medium text-sm mb-2">Locked</p>
                        <Button 
                          onClick={() => setShowSignupModal(true)}
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100 text-xs px-3 py-1"
                        >
                          Register
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {product.is_authenticated && (
                  <Badge className="absolute top-2 left-2 bg-black text-white text-xs font-medium tracking-wide">
                    AUTHENTICATED
                  </Badge>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (user) {
                      addToWishlist(product);
                    } else {
                      setShowSignupModal(true);
                    }
                  }}
                  className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                    user && isInWishlist(product.id) 
                      ? 'bg-red-500 text-white' 
                      : 'bg-white text-gray-600 hover:bg-red-500 hover:text-white'
                  }`}
                >
                  <Heart className={`w-3 h-3 ${user && isInWishlist(product.id) ? 'fill-current' : ''}`} />
                </button>
              </div>

              <CardHeader className="pb-4 pt-6">
                <div className="space-y-2">
                  <CardTitle className="text-lg font-serif text-black">{product.name}</CardTitle>
                  <CardDescription className="text-gray-600 font-medium tracking-wide uppercase text-sm">{product.brand}</CardDescription>
                  <div className="pt-2">
                    <p className="text-2xl font-serif text-black">{formatCurrency(product.price, user?.currency || 'GBP')}</p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0 space-y-6">
                <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
                
                {/* Environmental Impact */}
                <div className="bg-gray-50 p-4 space-y-3">
                  <h4 className="text-xs font-medium tracking-wide uppercase text-gray-800">Environmental Impact</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xl font-serif text-black">{product.water_saved_litres.toLocaleString()}L</p>
                      <p className="text-xs text-gray-600">Water Conserved</p>
                    </div>
                    <div>
                      <p className="text-xl font-serif text-black">{product.co2_saved_litres}L</p>
                      <p className="text-xs text-gray-600">CO₂ Reduced</p>
                    </div>
                  </div>
                </div>

                {/* Wallet Credits */}
                <div className="flex items-center justify-between pt-2">
                  <div>
                    <p className="text-sm text-gray-600">Earn {formatCurrency(product.points_earned / 100, user?.currency || 'GBP')} credit</p>
                  </div>
                </div>

                <Button 
                  className="w-full bg-black hover:bg-gray-900 text-white py-3 text-sm tracking-wide uppercase font-medium disabled:opacity-50"
                  onClick={() => {
                    if (user) {
                      addToCart(product);
                    } else {
                      setShowSignupModal(true);
                    }
                  }}
                >
                  {user ? 'Add to Cart' : 'Sign Up to Purchase'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No products found in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Sell Page Component
const SellPage = ({ 
  sellFormData, 
  setSellFormData, 
  handleSellRequest, 
  handleImageUpload, 
  removeImage, 
  isUploadingImages, 
  imageUploadError, 
  user 
}) => {
  return (
    <div className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-serif text-black mb-6">Consign Your Luxury</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            Transform your closet into credit while contributing to circular fashion. 
            Our authentication experts ensure fair valuations and seamless transactions.
          </p>
        </div>

        <Card className="bg-gray-50 border-0 shadow-none">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl font-serif text-black">Submit for Evaluation</CardTitle>
            <CardDescription className="text-gray-600">
              Upload 4-10 high-quality images and provide detailed information about your luxury item. We'll provide a quote within 24 hours.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <form onSubmit={handleSellRequest} className="space-y-8">
              
              {/* Image Upload Section */}
              <div className="space-y-4">
                <Label className="text-sm font-medium text-gray-800 tracking-wide uppercase flex items-center">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Item Images (4-10 required)
                </Label>
                
                {sellFormData.images.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      disabled={isUploadingImages}
                    />
                    <label htmlFor="image-upload" className="cursor-pointer block">
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600 text-lg mb-2">
                        {isUploadingImages ? 'Processing images...' : 'Click to upload images'}
                      </p>
                      <p className="text-gray-500 text-sm">
                        Upload 4-10 high-quality images (JPEG, PNG, WebP) • Max 5MB each
                      </p>
                      <p className="text-gray-500 text-xs mt-2">
                        Include different angles, details, authenticity cards, and packaging
                      </p>
                    </label>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {sellFormData.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image}
                            alt={`Item image ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        {sellFormData.images.length} of 10 images uploaded
                      </span>
                      {sellFormData.images.length >= 4 && (
                        <span className="text-green-600 flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Minimum requirement met
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {imageUploadError && (
                  <p className="text-red-600 text-sm flex items-center">
                    <X className="w-4 h-4 mr-1" />
                    {imageUploadError}
                  </p>
                )}
              </div>

              {/* Rest of the form would go here - truncated for brevity */}
              <Button 
                type="submit" 
                className="w-full bg-black hover:bg-gray-900 text-white py-4 text-sm tracking-wide uppercase font-medium disabled:opacity-50"
                disabled={sellFormData.images.length < 4 || isUploadingImages}
              >
                {isUploadingImages ? 'Processing Images...' : sellFormData.images.length < 4 ? `Upload ${4 - sellFormData.images.length} More Images` : 'Submit for Evaluation'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Seller Registration Page Component (with integrated product upload)
const SellerRegistrationPage = ({ API, axios }) => {
  const [step, setStep] = useState(1); // 1: Seller Registration, 2: Product Upload
  const [sellerFormData, setSellerFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+44', // UK as default
    business_name: '',
    business_type: 'individual',
    experience_level: 'beginner',
    expected_monthly_volume: '',
    password: ''
  });
  
  const [productFormData, setProductFormData] = useState({
    name: '',
    brand: '',
    model: '',
    category: '',
    condition: '',
    description: '',
    purchase_price: '',
    asking_price: '',
    purchase_year: '',
    authenticity_cards: false,
    original_packaging: false,
    images: []
  });
  
  const [authError, setAuthError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImageUploadError('');
    
    if (files.length < 4) {
      setImageUploadError('Please upload at least 4 images');
      return;
    }
    
    if (files.length > 10) {
      setImageUploadError('Maximum 10 images allowed');
      return;
    }
    
    setIsUploadingImages(true);
    
    Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          if (!file.type.startsWith('image/')) {
            reject(new Error(`${file.name} is not a valid image file`));
            return;
          }
          
          if (file.size > 5 * 1024 * 1024) {
            reject(new Error(`${file.name} is too large. Maximum 5MB allowed.`));
            return;
          }
          
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
          reader.readAsDataURL(file);
        });
      })
    )
    .then((imageDataUrls) => {
      setProductFormData({...productFormData, images: imageDataUrls});
      setIsUploadingImages(false);
    })
    .catch((error) => {
      setImageUploadError(error.message);
      setIsUploadingImages(false);
    });
  };

  const removeImage = (indexToRemove) => {
    const updatedImages = productFormData.images.filter((_, index) => index !== indexToRemove);
    setProductFormData({...productFormData, images: updatedImages});
    
    if (updatedImages.length < 4) {
      setImageUploadError('Minimum 4 images required');
    } else {
      setImageUploadError('');
    }
  };

  const handleSellerRegistration = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      // Combine country code with phone number
      const fullPhoneNumber = sellerFormData.countryCode + sellerFormData.phone;
      const submitData = {
        ...sellerFormData,
        phone: fullPhoneNumber
      };
      
      const response = await axios.post(`${API}/sellers/register`, submitData);
      setStep(2); // Move to product upload step
      setSuccessMessage('Seller account created! Now upload your first product for review.');
    } catch (error) {
      console.error('Error registering seller:', error);
      if (error.response?.data?.detail) {
        setAuthError(error.response.data.detail);
      } else {
        setAuthError('An error occurred during registration. Please try again.');
      }
    }
  };

  const handleProductUpload = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsSubmitting(true);
    
    if (productFormData.images.length < 4) {
      setImageUploadError('Please upload at least 4 images');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const submitData = {
        name: sellerFormData.name,
        email: sellerFormData.email,
        phone: sellerFormData.phone,
        item_type: productFormData.category,
        brand: productFormData.brand,
        model: productFormData.model,
        description: productFormData.description,
        condition: productFormData.condition,
        authenticity_cards: productFormData.authenticity_cards,
        original_packaging: productFormData.original_packaging,
        purchase_year: productFormData.purchase_year ? parseInt(productFormData.purchase_year) : null,
        estimated_value: productFormData.asking_price ? parseFloat(productFormData.asking_price) : null,
        images: productFormData.images
      };
      
      const response = await axios.post(`${API}/sell-requests`, submitData);
      
      setSuccessMessage('Perfect! Your product has been uploaded for review. Our authentication team will evaluate it within 24-48 hours and contact you with a quote.');
      
      // Clear form after successful submission
      setProductFormData({
        name: '',
        brand: '',
        model: '',
        category: '',
        condition: '',
        description: '',
        purchase_price: '',
        asking_price: '',
        purchase_year: '',
        authenticity_cards: false,
        original_packaging: false,
        images: []
      });
      
    } catch (error) {
      console.error('Error uploading product:', error);
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          setAuthError(error.response.data.detail.map(err => err.msg).join(', '));
        } else {
          setAuthError(error.response.data.detail);
        }
      } else {
        setAuthError('An error occurred while uploading your product. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-serif text-black mb-6">Sell Your Luxury Items</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            Join our network of sellers and earn competitive commissions on authenticated designer pieces.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 1 ? 'bg-black text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              1
            </div>
            <div className="w-16 h-0.5 bg-gray-200 mx-4"></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 2 ? 'bg-black text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              2
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <p className="text-green-700 text-sm font-medium">{successMessage}</p>
          </div>
        )}

        {authError && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <p className="text-red-700 text-sm font-medium">{authError}</p>
          </div>
        )}

        {step === 1 && (
          <Card className="bg-gray-50 border-0 shadow-none">
            <CardHeader className="pb-6">
              <CardTitle className="text-2xl font-serif text-black">Step 1: Create Seller Account</CardTitle>
              <CardDescription className="text-gray-600">
                Complete your seller profile to start earning commissions.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <form onSubmit={handleSellerRegistration} className="space-y-6">
                
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-black">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="seller-name" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Full Name</Label>
                      <Input
                        id="seller-name"
                        value={sellerFormData.name}
                        onChange={(e) => setSellerFormData({...sellerFormData, name: e.target.value})}
                        className="bg-white border-gray-200"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="seller-email" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Email Address</Label>
                      <Input
                        id="seller-email"
                        type="email"
                        value={sellerFormData.email}
                        onChange={(e) => setSellerFormData({...sellerFormData, email: e.target.value})}
                        className="bg-white border-gray-200"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="seller-phone" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Phone Number</Label>
                      <div className="flex gap-2">
                        <Select value={sellerFormData.countryCode} onValueChange={(value) => setSellerFormData({...sellerFormData, countryCode: value})}>
                          <SelectTrigger className="bg-white border-gray-200 w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[100]">
                            <SelectItem value="+44">🇬🇧 +44</SelectItem>
                            <SelectItem value="+1">🇺🇸 +1</SelectItem>
                            <SelectItem value="+33">🇫🇷 +33</SelectItem>
                            <SelectItem value="+49">🇩🇪 +49</SelectItem>
                            <SelectItem value="+39">🇮🇹 +39</SelectItem>
                            <SelectItem value="+34">🇪🇸 +34</SelectItem>
                            <SelectItem value="+31">🇳🇱 +31</SelectItem>
                            <SelectItem value="+32">🇧🇪 +32</SelectItem>
                            <SelectItem value="+41">🇨🇭 +41</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          id="seller-phone"
                          type="tel"
                          value={sellerFormData.phone}
                          onChange={(e) => setSellerFormData({...sellerFormData, phone: e.target.value})}
                          placeholder="7700123456"
                          className="bg-white border-gray-200 flex-1"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="seller-password" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Password</Label>
                      <div className="relative">
                        <Input
                          id="seller-password"
                          type={showPassword ? "text" : "password"}
                          value={sellerFormData.password}
                          onChange={(e) => setSellerFormData({...sellerFormData, password: e.target.value})}
                          className="bg-white border-gray-200 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-black">Business Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="business-type" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Business Type</Label>
                      <Select value={sellerFormData.business_type} onValueChange={(value) => setSellerFormData({...sellerFormData, business_type: value})}>
                        <SelectTrigger className="bg-white border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="individual">Individual Seller</SelectItem>
                          <SelectItem value="business">Business/Company</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="business-name" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Business Name (Optional)</Label>
                      <Input
                        id="business-name"
                        value={sellerFormData.business_name}
                        onChange={(e) => setSellerFormData({...sellerFormData, business_name: e.target.value})}
                        placeholder="Your business or store name"
                        className="bg-white border-gray-200"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="experience-level" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Experience Level</Label>
                      <Select value={sellerFormData.experience_level} onValueChange={(value) => setSellerFormData({...sellerFormData, experience_level: value})}>
                        <SelectTrigger className="bg-white border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner (0-1 years)</SelectItem>
                          <SelectItem value="intermediate">Intermediate (1-5 years)</SelectItem>
                          <SelectItem value="experienced">Experienced (5+ years)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="monthly-volume" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Expected Monthly Volume</Label>
                      <Select value={sellerFormData.expected_monthly_volume} onValueChange={(value) => setSellerFormData({...sellerFormData, expected_monthly_volume: value})}>
                        <SelectTrigger className="bg-white border-gray-200">
                          <SelectValue placeholder="Select volume" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-5">1-5 items per month</SelectItem>
                          <SelectItem value="6-20">6-20 items per month</SelectItem>
                          <SelectItem value="21-50">21-50 items per month</SelectItem>
                          <SelectItem value="50+">50+ items per month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-black hover:bg-gray-900 text-white py-4 text-sm tracking-wide uppercase font-medium"
                >
                  Create Seller Account & Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="bg-gray-50 border-0 shadow-none">
            <CardHeader className="pb-6">
              <CardTitle className="text-2xl font-serif text-black">Step 2: Upload Your First Product</CardTitle>
              <CardDescription className="text-gray-600">
                Upload 4-10 high-quality images and provide detailed information about your luxury item.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <form onSubmit={handleProductUpload} className="space-y-8">
                
                {/* Image Upload Section */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium text-gray-800 tracking-wide uppercase flex items-center">
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Item Images (4-10 required)
                  </Label>
                  
                  {productFormData.images.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                        disabled={isUploadingImages}
                      />
                      <label htmlFor="image-upload" className="cursor-pointer block">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600 text-lg mb-2">
                          {isUploadingImages ? 'Processing images...' : 'Click to upload images'}
                        </p>
                        <p className="text-gray-500 text-sm">
                          Upload 4-10 high-quality images (JPEG, PNG, WebP) • Max 5MB each
                        </p>
                        <p className="text-gray-500 text-xs mt-2">
                          Include different angles, details, authenticity cards, and packaging
                        </p>
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {productFormData.images.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image}
                              alt={`Item image ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        
                        {productFormData.images.length < 10 && (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex items-center justify-center hover:border-gray-400 transition-colors">
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={(e) => {
                                const newFiles = Array.from(e.target.files);
                                const currentCount = productFormData.images.length;
                                const remainingSlots = 10 - currentCount;
                                
                                if (newFiles.length > remainingSlots) {
                                  setImageUploadError(`You can only add ${remainingSlots} more image(s)`);
                                  return;
                                }
                                
                                setIsUploadingImages(true);
                                Promise.all(
                                  newFiles.map((file) => {
                                    return new Promise((resolve, reject) => {
                                      if (!file.type.startsWith('image/')) {
                                        reject(new Error(`${file.name} is not a valid image file`));
                                        return;
                                      }
                                      if (file.size > 5 * 1024 * 1024) {
                                        reject(new Error(`${file.name} is too large. Maximum 5MB allowed.`));
                                        return;
                                      }
                                      const reader = new FileReader();
                                      reader.onload = () => resolve(reader.result);
                                      reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
                                      reader.readAsDataURL(file);
                                    });
                                  })
                                )
                                .then((newImages) => {
                                  setProductFormData({
                                    ...productFormData, 
                                    images: [...productFormData.images, ...newImages]
                                  });
                                  setIsUploadingImages(false);
                                  setImageUploadError('');
                                })
                                .catch((error) => {
                                  setImageUploadError(error.message);
                                  setIsUploadingImages(false);
                                });
                              }}
                              className="hidden"
                              id="additional-images"
                              disabled={isUploadingImages}
                            />
                            <label htmlFor="additional-images" className="cursor-pointer flex flex-col items-center">
                              <Plus className="w-6 h-6 text-gray-400" />
                              <span className="text-xs text-gray-500 mt-1">Add More</span>
                            </label>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">
                          {productFormData.images.length} of 10 images uploaded
                        </span>
                        {productFormData.images.length >= 4 && (
                          <span className="text-green-600 flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Minimum requirement met
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {imageUploadError && (
                    <p className="text-red-600 text-sm flex items-center">
                      <X className="w-4 h-4 mr-1" />
                      {imageUploadError}
                    </p>
                  )}
                </div>

                {/* Product Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="product-name" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Product Name</Label>
                    <Input
                      id="product-name"
                      value={productFormData.name}
                      onChange={(e) => setProductFormData({...productFormData, name: e.target.value})}
                      placeholder="e.g. Classic Flap Bag Medium"
                      className="bg-white border-gray-200"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-brand" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Designer/Brand</Label>
                    <Input
                      id="product-brand"
                      value={productFormData.brand}
                      onChange={(e) => setProductFormData({...productFormData, brand: e.target.value})}
                      placeholder="e.g. Chanel, Hermès, Louis Vuitton"
                      className="bg-white border-gray-200"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="product-model" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Model/Style</Label>
                    <Input
                      id="product-model"
                      value={productFormData.model}
                      onChange={(e) => setProductFormData({...productFormData, model: e.target.value})}
                      placeholder="e.g. Classic Flap, Birkin 35, Neverfull MM"
                      className="bg-white border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="item-category" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Item Category</Label>
                    <Select value={productFormData.category} onValueChange={(value) => setProductFormData({...productFormData, category: value})}>
                      <SelectTrigger className="bg-white border-gray-200">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bags">Handbags</SelectItem>
                        <SelectItem value="watches">Timepieces</SelectItem>
                        <SelectItem value="accessories">Accessories</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="product-condition" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Condition</Label>
                    <Select value={productFormData.condition} onValueChange={(value) => setProductFormData({...productFormData, condition: value})}>
                      <SelectTrigger className="bg-white border-gray-200">
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excellent">Excellent - Like new, minimal signs of wear</SelectItem>
                        <SelectItem value="very-good">Very Good - Light wear, well maintained</SelectItem>
                        <SelectItem value="good">Good - Moderate wear, fully functional</SelectItem>
                        <SelectItem value="fair">Fair - Visible wear, may need restoration</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="asking-price" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Your Asking Price (GBP)</Label>
                    <Input
                      id="asking-price"
                      type="number"
                      value={productFormData.asking_price}
                      onChange={(e) => setProductFormData({...productFormData, asking_price: e.target.value})}
                      placeholder="Your desired selling price"
                      className="bg-white border-gray-200"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="purchase-price" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Original Purchase Price (Optional)</Label>
                    <Input
                      id="purchase-price"
                      type="number"
                      value={productFormData.purchase_price}
                      onChange={(e) => setProductFormData({...productFormData, purchase_price: e.target.value})}
                      placeholder="What you originally paid"
                      className="bg-white border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchase-year" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Purchase Year (Optional)</Label>
                    <Input
                      id="purchase-year"
                      type="number"
                      min="1990"
                      max={new Date().getFullYear()}
                      value={productFormData.purchase_year}
                      onChange={(e) => setProductFormData({...productFormData, purchase_year: e.target.value})}
                      placeholder="e.g. 2020"
                      className="bg-white border-gray-200"
                    />
                  </div>
                </div>

                {/* Authenticity & Packaging */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium text-gray-800 tracking-wide uppercase">Authenticity & Packaging</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="authenticity-cards"
                        checked={productFormData.authenticity_cards}
                        onChange={(e) => setProductFormData({...productFormData, authenticity_cards: e.target.checked})}
                        className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                      />
                      <label htmlFor="authenticity-cards" className="text-sm text-gray-700">
                        Authenticity cards/certificates included
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="original-packaging"
                        checked={productFormData.original_packaging}
                        onChange={(e) => setProductFormData({...productFormData, original_packaging: e.target.checked})}
                        className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                      />
                      <label htmlFor="original-packaging" className="text-sm text-gray-700">
                        Original box/dust bag included
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="product-description" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Item Details & Condition</Label>
                  <Textarea
                    id="product-description"
                    value={productFormData.description}
                    onChange={(e) => setProductFormData({...productFormData, description: e.target.value})}
                    placeholder="Describe the item's detailed condition, any flaws, maintenance history, and special features. The more detail you provide, the more accurate our valuation will be..."
                    className="bg-white border-gray-200 min-h-[120px]"
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-black hover:bg-gray-900 text-white py-4 text-sm tracking-wide uppercase font-medium disabled:opacity-50"
                  disabled={productFormData.images.length < 4 || isUploadingImages || isSubmitting}
                >
                  {isSubmitting ? 'Submitting for Review...' : 
                   isUploadingImages ? 'Processing Images...' : 
                   productFormData.images.length < 4 ? `Upload ${4 - productFormData.images.length} More Images` : 
                   'Submit for Authentication & Review'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Seller Dashboard Page Component
const SellerDashboardPage = ({ user, formatCurrency, goToHome }) => {
  const [products, setProducts] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    name: '',
    brand: '',
    model: '',
    category: '',
    condition: '',
    description: '',
    purchase_price: '',
    asking_price: '',
    purchase_year: '',
    authenticity_cards: false,
    original_packaging: false,
    images: []
  });
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [authError, setAuthError] = useState('');

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImageUploadError('');
    
    if (files.length < 4) {
      setImageUploadError('Please upload at least 4 images');
      return;
    }
    
    if (files.length > 10) {
      setImageUploadError('Maximum 10 images allowed');
      return;
    }
    
    setIsUploadingImages(true);
    
    Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          if (!file.type.startsWith('image/')) {
            reject(new Error(`${file.name} is not a valid image file`));
            return;
          }
          
          if (file.size > 5 * 1024 * 1024) {
            reject(new Error(`${file.name} is too large. Maximum 5MB allowed.`));
            return;
          }
          
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
          reader.readAsDataURL(file);
        });
      })
    )
    .then((imageDataUrls) => {
      setUploadFormData({...uploadFormData, images: imageDataUrls});
      setIsUploadingImages(false);
    })
    .catch((error) => {
      setImageUploadError(error.message);
      setIsUploadingImages(false);
    });
  };

  const handleProductUpload = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    if (uploadFormData.images.length < 4) {
      setImageUploadError('Please upload at least 4 images');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/seller/products`, {
        ...uploadFormData,
        seller_email: user.email,
        status: 'pending_review'
      });
      
      setSuccessMessage('Product uploaded successfully! It will be reviewed within 24-48 hours.');
      setShowUploadModal(false);
      setUploadFormData({
        name: '',
        brand: '',
        model: '',
        category: '',
        condition: '',
        description: '',
        purchase_price: '',
        asking_price: '',
        purchase_year: '',
        authenticity_cards: false,
        original_packaging: false,
        images: []
      });
      
      // Refresh products list
      fetchSellerProducts();
      
    } catch (error) {
      console.error('Error uploading product:', error);
      setAuthError('Error uploading product. Please try again.');
    }
  };

  const fetchSellerProducts = async () => {
    try {
      const response = await axios.get(`${API}/seller/products/${user.email}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching seller products:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchSellerProducts();
    }
  }, [user]);

  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Dashboard Header */}
        <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-serif text-black mb-2">Seller Dashboard</h1>
              <p className="text-gray-600">Welcome back, {user?.name || 'Seller'}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-serif text-black">{formatCurrency(user?.seller_credits || 0, user?.currency || 'GBP')}</div>
              <p className="text-sm text-gray-600">Commission Credits</p>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <p className="text-green-700 text-sm font-medium">{successMessage}</p>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <div className="text-2xl font-serif text-black mb-2">{products.filter(p => p.status === 'active').length}</div>
              <p className="text-sm text-gray-600">Active Listings</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <div className="text-2xl font-serif text-black mb-2">{products.filter(p => p.status === 'sold').length}</div>
              <p className="text-sm text-gray-600">Items Sold</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <div className="text-2xl font-serif text-black mb-2">{products.filter(p => p.status === 'pending_review').length}</div>
              <p className="text-sm text-gray-600">Under Review</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <div className="text-2xl font-serif text-black mb-2">{formatCurrency(user?.total_earnings || 0, user?.currency || 'GBP')}</div>
              <p className="text-sm text-gray-600">Total Earnings</p>
            </CardContent>
          </Card>
        </div>

        {/* Upload Product Button */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-serif text-black">Your Products</h2>
          <Button 
            onClick={() => setShowUploadModal(true)}
            className="bg-black hover:bg-gray-900 text-white px-6 py-3 text-sm tracking-wide uppercase font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload Product
          </Button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <Card key={product.id} className="bg-white border-0 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="relative overflow-hidden bg-gray-50">
                <img 
                  src={product.images?.[0] || '/placeholder-image.jpg'} 
                  alt={product.name}
                  className="w-full h-48 object-cover"
                />
                <Badge 
                  className={`absolute top-4 left-4 text-xs font-medium tracking-wide ${
                    product.status === 'active' ? 'bg-green-500 text-white' :
                    product.status === 'sold' ? 'bg-blue-500 text-white' :
                    product.status === 'pending_review' ? 'bg-yellow-500 text-white' :
                    'bg-gray-500 text-white'
                  }`}
                >
                  {product.status.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>

              <CardHeader className="pb-4 pt-6">
                <CardTitle className="text-lg font-serif text-black">{product.name}</CardTitle>
                <CardDescription className="text-gray-600 font-medium tracking-wide uppercase text-sm">{product.brand}</CardDescription>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-sm text-gray-600">Asking Price</p>
                    <p className="text-xl font-serif text-black">{formatCurrency(product.asking_price, user?.currency || 'GBP')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Commission</p>
                    <p className="text-lg font-semibold text-green-600">{formatCurrency(product.commission_earned || 0, user?.currency || 'GBP')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-2">No products yet</h3>
            <p className="text-gray-600 mb-6">Upload your first luxury item to get started</p>
            <Button 
              onClick={() => setShowUploadModal(true)}
              className="bg-black hover:bg-gray-900 text-white px-6 py-3 text-sm tracking-wide uppercase font-medium"
            >
              Upload Your First Product
            </Button>
          </div>
        )}
      </div>

      {/* Upload Product Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-black">Upload Product for Review</DialogTitle>
            <DialogDescription className="text-gray-600">
              Upload 4-10 high-quality images and provide detailed product information for authentication.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleProductUpload} className="space-y-6">
            {/* Image Upload Section - Similar to sell page but for seller dashboard */}
            {/* Product Details Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="product-name" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Product Name</Label>
                <Input
                  id="product-name"
                  value={uploadFormData.name}
                  onChange={(e) => setUploadFormData({...uploadFormData, name: e.target.value})}
                  placeholder="e.g. Classic Flap Bag Medium"
                  className="bg-white border-gray-200"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product-brand" className="text-sm font-medium text-gray-800 tracking-wide uppercase">Brand</Label>
                <Input
                  id="product-brand"
                  value={uploadFormData.brand}
                  onChange={(e) => setUploadFormData({...uploadFormData, brand: e.target.value})}
                  placeholder="e.g. Chanel"
                  className="bg-white border-gray-200"
                  required
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <Button 
                type="submit" 
                className="flex-1 bg-black hover:bg-gray-900 text-white py-3 text-sm tracking-wide uppercase font-medium"
                disabled={uploadFormData.images.length < 4 || isUploadingImages}
              >
                {isUploadingImages ? 'Processing Images...' : 'Submit for Review'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowUploadModal(false)}
                className="px-8 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const formatCurrency = (amount, currency = 'GBP') => {
  const symbols = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    CAD: 'C$',
    AUD: 'A$'
  };
  return `${symbols[currency] || '£'}${amount.toLocaleString()}`;
};

// Rewards Ecosystem Page Component
const RewardsPage = ({ user, onBack, onSignOut }) => {
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchRewardsData();
    }
  }, [user]);

  const fetchRewardsData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, partnersRes, redemptionsRes] = await Promise.all([
        axios.get(`${API}/redemption-categories`),
        axios.get(`${API}/partner-brands`),
        axios.get(`${API}/redemptions/${user.email}`)
      ]);
      
      setCategories(categoriesRes.data);
      setPartners(partnersRes.data);
      setRedemptions(redemptionsRes.data);
    } catch (error) {
      console.error('Error fetching rewards data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (partnerId, creditsAmount) => {
    try {
      const response = await axios.post(`${API}/redeem`, {
        user_email: user.email,
        partner_id: partnerId,  
        credits_amount: creditsAmount
      });
      
      alert(`Redemption successful! Reference: ${response.data.reference_code}`);
      fetchRewardsData();
      
      // Update user data
      const userResponse = await axios.get(`${API}/users/${user.email}`);
      // Update user state in parent component would need callback
      window.location.reload(); // Simple refresh for demo
    } catch (error) {
      console.error('Error redeeming credits:', error);
      alert('Error processing redemption. Please try again.');
    }
  };

  const getFilteredPartners = () => {
    if (selectedCategory === 'all') return partners;
    return partners.filter(partner => partner.category === selectedCategory);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      transport: Car,
      fashion: Shirt,
      lifestyle: Leaf,
      energy: Zap,
      marketplace: ShoppingBag
    };
    return icons[category] || Gift;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button onClick={onBack} className="text-gray-600 hover:text-black">
                <ArrowRight className="w-5 h-5 rotate-180" />
              </button>
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-serif text-black tracking-wide">
                PERFECT CIRCLE
              </h1>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 bg-gray-50 px-4 py-2 rounded-sm">
                <Wallet className="w-4 h-4 text-gray-700" />
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(user.wallet_points / 100, user.currency)} Credit
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={onSignOut}
                className="text-gray-700 hover:text-black"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Rewards Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="mb-12">
          <h2 className="text-5xl font-serif text-black mb-4">Rewards Ecosystem</h2>
          <p className="text-gray-600 text-lg max-w-3xl">
            Transform your sustainability credits into real-world rewards. From electric rides to eco-fashion, 
            your environmental impact unlocks a circular economy of conscious choices.
          </p>
        </div>

        {/* Wallet Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <Card className="bg-black text-white border-0">
            <CardContent className="p-6 text-center">
              <Wallet className="w-8 h-8 mx-auto mb-3" />
              <div className="text-3xl font-serif mb-2">{formatCurrency(user.wallet_points / 100, user.currency)}</div>
              <p className="text-gray-300 text-sm uppercase tracking-wide">Available Credit</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-0">
            <CardContent className="p-6 text-center">
              <CreditCard className="w-8 h-8 mx-auto mb-3 text-gray-600" />
              <div className="text-3xl font-serif text-black mb-2">{formatCurrency(user.total_redeemed || 0, user.currency)}</div>
              <p className="text-gray-600 text-sm uppercase tracking-wide">Total Redeemed</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-0">
            <CardContent className="p-6 text-center">
              <Gift className="w-8 h-8 mx-auto mb-3 text-gray-600" />
              <div className="text-3xl font-serif text-black mb-2">{redemptions.length}</div>
              <p className="text-gray-600 text-sm uppercase tracking-wide">Redemptions</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="marketplace" className="space-y-8">
          <TabsList className="grid w-full grid-cols-2 bg-gray-100">
            <TabsTrigger value="marketplace" className="text-sm tracking-wide uppercase font-medium">Partner Marketplace</TabsTrigger>
            <TabsTrigger value="history" className="text-sm tracking-wide uppercase font-medium">Redemption History</TabsTrigger>
          </TabsList>

          <TabsContent value="marketplace" className="space-y-8">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-4 justify-center">
              {['all', 'transport', 'fashion', 'lifestyle', 'energy'].map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "ghost"}
                  size="sm"
                  className={`px-6 py-2 text-sm tracking-wide uppercase font-medium ${
                    selectedCategory === category 
                      ? 'bg-black text-white hover:bg-gray-900' 
                      : 'text-gray-700 hover:text-black hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'all' ? 'All Partners' : category.charAt(0).toUpperCase() + category.slice(1)}
                </Button>
              ))}
            </div>

            {/* Partner Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {getFilteredPartners().map((partner) => {
                const IconComponent = getCategoryIcon(partner.category);
                const canRedeem = user.wallet_points >= partner.min_redemption;
                
                return (
                  <Card key={partner.id} className="group bg-white border-0 shadow-sm hover:shadow-lg transition-all duration-300">
                    <div className="relative overflow-hidden bg-gray-50">
                      <img 
                        src={partner.logo_url} 
                        alt={partner.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <Badge className="absolute top-4 left-4 bg-white text-black text-xs font-medium">
                        {partner.sustainability_score}% Sustainable
                      </Badge>
                    </div>

                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <CardTitle className="text-lg font-serif text-black">{partner.name}</CardTitle>
                          <div className="flex items-center space-x-2">
                            <IconComponent className="w-4 h-4 text-gray-600" />
                            <CardDescription className="text-gray-600 font-medium tracking-wide uppercase text-xs">
                              {partner.category}
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0 space-y-6">
                      <p className="text-gray-600 text-sm leading-relaxed">{partner.description}</p>
                      
                      <div className="bg-gray-50 p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-600 uppercase tracking-wide">Exchange Rate</span>
                          <span className="text-sm font-medium">{(partner.redemption_rate * 100).toFixed(0)}p per credit</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-600 uppercase tracking-wide">Minimum</span>
                          <span className="text-sm font-medium">{partner.min_redemption} credits</span>
                        </div>
                      </div>

                      <Button 
                        className={`w-full py-3 text-sm tracking-wide uppercase font-medium ${
                          canRedeem 
                            ? 'bg-black hover:bg-gray-900 text-white' 
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                        onClick={() => canRedeem && handleRedeem(partner.id, partner.min_redemption)}
                        disabled={!canRedeem}
                      >
                        {canRedeem ? `Redeem ${partner.min_redemption} Credits` : 'Insufficient Credits'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="history" className="space-y-8">
            {redemptions.length === 0 ? (
              <div className="text-center py-12">
                <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">No redemptions yet</p>
                <Button onClick={() => document.querySelector('[value="marketplace"]').click()} className="bg-black hover:bg-gray-900 text-white">
                  Explore Partners
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {redemptions.map((redemption) => (
                  <Card key={redemption.id} className="border-0 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <h4 className="text-lg font-serif text-black">{redemption.partner_name}</h4>
                          <p className="text-gray-600 text-sm">
                            {formatDate(redemption.redemption_date)} • Ref: {redemption.reference_code}
                          </p>
                          <div className="flex items-center space-x-4">
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {redemption.status}
                            </Badge>
                            <span className="text-sm text-gray-600 uppercase tracking-wide">
                              {redemption.category}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-serif text-black">{formatCurrency(redemption.dollar_value, redemption.currency)}</p>
                          <p className="text-sm text-gray-600">{redemption.credits_used} credits</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Account Page Component
const AccountPage = ({ user, onBack, onSignOut, onViewRewards, onUpdateUser }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(user.currency);

  useEffect(() => {
    if (user) {
      fetchOrders();
      fetchCurrencies();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders/${user.email}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const handleCurrencyUpdate = async () => {
    try {
      const response = await axios.put(`${API}/users/${user.email}/currency`, {
        currency: selectedCurrency
      });
      onUpdateUser(response.data);
      setShowCurrencyModal(false);
      alert('Currency preference updated successfully!');
    } catch (error) {
      console.error('Error updating currency:', error);
      alert('Error updating currency preference. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button onClick={onBack} className="text-gray-600 hover:text-black">
                <ArrowRight className="w-5 h-5 rotate-180" />
              </button>
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-serif text-black tracking-wide">
                PERFECT CIRCLE
              </h1>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 bg-gray-50 px-4 py-2 rounded-sm">
                <Wallet className="w-4 h-4 text-gray-700" />
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(user.wallet_points / 100, user.currency)} Credit
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowCurrencyModal(true)}
                className="text-gray-700 hover:text-black"
              >
                <Globe className="w-4 h-4 mr-2" />
                {user.currency}
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={onSignOut}
                className="text-gray-700 hover:text-black"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Account Content */}
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        {/* Account Header */}
        <div className="mb-12">
          <h2 className="text-5xl font-serif text-black mb-4">My Account</h2>
          <p className="text-gray-600 text-lg">Welcome back, {user.name}</p>
        </div>

        {/* Account Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
          <Card className="bg-gray-50 border-0">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-serif text-black mb-2">{user.total_orders}</div>
              <p className="text-gray-600 text-sm uppercase tracking-wide">Orders</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-0">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-serif text-black mb-2">{formatCurrency(user.wallet_points / 100, user.currency)}</div>
              <p className="text-gray-600 text-sm uppercase tracking-wide">Wallet Credit</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-0">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-serif text-black mb-2">{user.total_water_saved?.toLocaleString() || 0}L</div>
              <p className="text-gray-600 text-sm uppercase tracking-wide">Water Conserved</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-0">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-serif text-black mb-2">{user.total_co2_saved?.toFixed(0) || 0}L</div>
              <p className="text-gray-600 text-sm uppercase tracking-wide">CO₂ Reduced</p>
            </CardContent>
          </Card>
        </div>

        {/* Rewards CTA */}
        <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-0 mb-12">
          <CardContent className="p-8 text-center">
            <Gift className="w-12 h-12 mx-auto mb-4 text-emerald-600" />
            <h3 className="text-2xl font-serif text-black mb-3">Rewards Ecosystem</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Transform your sustainability credits into real rewards. From electric rides to eco-fashion, 
              discover our partner network designed for conscious consumers.
            </p>
            <Button 
              onClick={onViewRewards}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 text-sm tracking-wide uppercase font-medium"
            >
              Explore Rewards
            </Button>
          </CardContent>
        </Card>

        {/* Order History */}
        <div>
          <h3 className="text-3xl font-serif text-black mb-8">Order History</h3>
          
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-4">No orders yet</p>
              <Button onClick={onBack} className="bg-black hover:bg-gray-900 text-white">
                Start Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <Card key={order.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Product Image */}
                      <div className="w-full lg:w-48 h-48 bg-gray-50 overflow-hidden">
                        <img 
                          src={order.product_image_url} 
                          alt={order.product_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Order Details */}
                      <div className="flex-1 space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-xl font-serif text-black">{order.product_name}</h4>
                            <p className="text-gray-600 font-medium tracking-wide uppercase text-sm">{order.product_brand}</p>
                            <p className="text-sm text-gray-500 mt-2">Order placed on {formatDate(order.order_date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-serif text-black">{formatCurrency(order.product_price, order.currency)}</p>
                            <Badge className="bg-green-100 text-green-800 text-xs mt-2">
                              {order.status}
                            </Badge>
                          </div>
                        </div>

                        <Separator />

                        {/* Environmental Impact */}
                        <div className="bg-gray-50 p-4 space-y-3">
                          <h5 className="text-xs font-medium tracking-wide uppercase text-gray-800">Environmental Impact</h5>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-lg font-serif text-black">{order.water_saved.toLocaleString()}L</p>
                              <p className="text-xs text-gray-600">Water Conserved</p>
                            </div>   
                            <div>
                              <p className="text-lg font-serif text-black">{order.co2_saved}L</p>
                              <p className="text-xs text-gray-600">CO₂ Reduced</p>
                            </div>
                            <div>
                              <p className="text-lg font-serif text-black">{formatCurrency(order.points_earned / 100, order.currency)}</p>
                              <p className="text-xs text-gray-600">Credit Earned</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Currency Selection Modal */}
      <Dialog open={showCurrencyModal} onOpenChange={setShowCurrencyModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader className="text-center space-y-4">
            <DialogTitle className="text-2xl font-serif text-black">
              Currency Preference
            </DialogTitle>
            <DialogDescription className="text-gray-600 leading-relaxed">
              Choose your preferred currency for displaying prices and wallet credits.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-6">
            <div className="space-y-4">
              {currencies.map((currency) => (
                <button
                  key={currency.code}
                  onClick={() => setSelectedCurrency(currency.code)}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${
                    selectedCurrency === currency.code
                      ? 'border-black bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{currency.code}</p>
                      <p className="text-sm text-gray-600">
                        {currency.code === 'GBP' && 'British Pound'}
                        {currency.code === 'USD' && 'US Dollar'}
                        {currency.code === 'EUR' && 'Euro'}
                        {currency.code === 'CAD' && 'Canadian Dollar'}
                        {currency.code === 'AUD' && 'Australian Dollar'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{currency.symbol}</p>
                      <p className="text-sm text-gray-600">
                        {currency.code === 'USD' ? 'Base' : `${currency.rate} to 1 USD`}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex space-x-4">
              <Button 
                onClick={handleCurrencyUpdate}
                className="flex-1 bg-black hover:bg-gray-900 text-white py-3 text-sm tracking-wide uppercase font-medium"
              >
                Update Currency
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowCurrencyModal(false)}
                className="px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main App Component
function App() {
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'shop', 'sell', 'seller-registration', 'commission', 'credits', 'account', 'rewards', 'reset-password'
  const [shopCategory, setShopCategory] = useState('all'); // 'all', 'women', 'men', 'kids', 'unisex'
  const [selectedCategory, setSelectedCategory] = useState('all'); // for product filtering
  const [wishlist, setWishlist] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [user, setUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetUserType, setResetUserType] = useState('user');
  const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutData, setCheckoutData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'United Kingdom',
    deliveryMethod: 'standard',
    paymentMethod: 'card'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    currency: 'GBP',
    countryCode: '+44'
  });
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [sellFormData, setSellFormData] = useState({
    name: '',
    email: '',
    phone: '',
    item_type: '',
    brand: '',
    model: '',
    description: '',
    condition: '',
    authenticity_cards: false,
    original_packaging: false,
    purchase_year: '',
    estimated_value: '',
    images: []
  });
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  useEffect(() => {
    // Check for saved user data on app initialization
    const savedUser = localStorage.getItem('perfectCircleUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('perfectCircleUser');
      }
    }
    
    // Check for saved wishlist
    const savedWishlist = localStorage.getItem('perfectCircleWishlist');
    if (savedWishlist) {
      try {
        const wishlistData = JSON.parse(savedWishlist);
        setWishlist(wishlistData);
      } catch (error) {
        console.error('Error parsing saved wishlist:', error);
        localStorage.removeItem('perfectCircleWishlist');
      }
    }
    
    // Check for reset password token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const type = urlParams.get('type');
    if (token) {
      setResetToken(token);
      setResetUserType(type || 'user');
      setCurrentPage('reset-password');
    }
    
    fetchCurrencies();
  }, []);

  useEffect(() => {
    if (currentPage === 'home' || currentPage === 'shop') {
      fetchProducts();
      seedProducts();
      seedPartners();
      // Show signup modal after 5 seconds if no user (only on home page)
      if (!user && currentPage === 'home') {
        const timer = setTimeout(() => {
          setShowSignupModal(true);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [currentPage, user]);

  // Wishlist functions
  const addToWishlist = (product) => {
    const isAlreadyInWishlist = wishlist.some(item => item.id === product.id);
    if (!isAlreadyInWishlist) {
      const updatedWishlist = [...wishlist, product];
      setWishlist(updatedWishlist);
      localStorage.setItem('perfectCircleWishlist', JSON.stringify(updatedWishlist));
      setSuccessMessage(`${product.name} added to your wishlist!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      setAuthError('Item is already in your wishlist');
      setTimeout(() => setAuthError(''), 3000);
    }
  };

  const removeFromWishlist = (productId) => {
    const updatedWishlist = wishlist.filter(item => item.id !== productId);
    setWishlist(updatedWishlist);
    localStorage.setItem('perfectCircleWishlist', JSON.stringify(updatedWishlist));
  };

  const isInWishlist = (productId) => {
    return wishlist.some(item => item.id === productId);
  };

  // Navigation functions
  const goToHome = () => {
    setCurrentPage('home');
    setShopCategory('all');
    setSelectedCategory('all');
  };

  const goToShop = (category = 'all') => {
    setCurrentPage('shop');
    setShopCategory(category);
    setSelectedCategory('all');
  };

  const goToSell = () => {
    setCurrentPage('sell');
  };

  const goToSellerRegistration = () => {
    setCurrentPage('seller-registration');
  };

  // Cart management functions
  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    setSuccessMessage(`${product.name} added to cart!`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item => 
        item.id === productId 
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const proceedToCheckout = () => {
    if (cart.length === 0) {
      setSuccessMessage('Your cart is empty!');
      setTimeout(() => setSuccessMessage(''), 3000);
      return;
    }
    setShowCart(false);
    setShowCheckout(true);
  };

  const goToSellerDashboard = () => {
    setCurrentPage('seller-dashboard');
  };

  const goToCommission = () => {
    setCurrentPage('commission');
  };

  const goToCredits = () => {
    setCurrentPage('credits');
  };

  const goToAccount = () => {
    setCurrentPage('account');
  };

  const goToRewards = () => {
    setCurrentPage('rewards');
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const seedProducts = async () => {
    try {
      await axios.post(`${API}/seed-products`);
    } catch (error) {
      console.error('Error seeding products:', error);
    }
  };

  const seedPartners = async () => {
    try {
      await axios.post(`${API}/seed-partners`);
    } catch (error) {
      console.error('Error seeding partners:', error);
    }
  };

  const fetchProducts = async (category = null) => {
    try {
      const currency = user?.currency || 'GBP';
      const url = category && category !== 'all' 
        ? `${API}/products?category=${category}&currency=${currency}` 
        : `${API}/products?currency=${currency}`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    fetchProducts(category);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      // Combine country code with phone number
      const fullPhoneNumber = formData.countryCode + formData.phone;
      const submitData = {
        ...formData,
        phone: fullPhoneNumber
      };
      
      const response = await axios.post(`${API}/users/register`, submitData);
      const userData = response.data;
      setUser(userData);
      
      // Save user data to localStorage for persistence
      localStorage.setItem('perfectCircleUser', JSON.stringify(userData));
      
      setShowSignupModal(false);
      setFormData({ name: '', email: '', phone: '', password: '', currency: 'GBP', countryCode: '+44' });
      
      // Show success message
      setSuccessMessage(
        `Welcome to Perfect Circle! You've successfully registered and saved 25% on your first order. Start shopping to earn wallet credits!`
      );
      
      // Clear success message after 8 seconds
      setTimeout(() => setSuccessMessage(''), 8000);
      
      // Refresh products with user's currency
      fetchProducts(selectedCategory);
    } catch (error) {
      console.error('Error signing up:', error);
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          setAuthError(error.response.data.detail.map(err => err.msg).join(', '));
        } else {
          setAuthError(error.response.data.detail);
        }
      } else {
        setAuthError('An error occurred during registration. Please try again.');
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await axios.post(`${API}/users/login`, loginData);
      const userData = response.data;
      setUser(userData);
      
      // Save user data to localStorage for persistence
      localStorage.setItem('perfectCircleUser', JSON.stringify(userData));
      
      setShowLoginModal(false);
      setLoginData({ email: '', password: '' });
      
      // Show welcome back message
      setSuccessMessage(`Welcome back, ${userData.name}! Your wallet has ${formatCurrency(userData.wallet_points / 100, userData.currency)} in credits.`);
      
      // Clear success message after 8 seconds  
      setTimeout(() => setSuccessMessage(''), 8000);
      
      // Refresh products with user's currency
      fetchProducts(selectedCategory);
    } catch (error) {
      console.error('Error logging in:', error);
      if (error.response?.data?.detail) {
        setAuthError(error.response.data.detail);
      } else {
        setAuthError('An error occurred during login. Please try again.');
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    setSuccessMessage('');
    setForgotPasswordLoading(true);
    
    try {
      await axios.post(`${API}/forgot-password`, {
        email: forgotPasswordEmail,
        user_type: "user"
      });
      
      setSuccessMessage('Password reset instructions have been sent to your email.');
      setShowForgotPasswordModal(false);
      setForgotPasswordEmail('');
      
      // Clear success message after 10 seconds
      setTimeout(() => setSuccessMessage(''), 10000);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      if (error.response?.data?.detail) {
        setAuthError(error.response.data.detail);
      } else {
        setAuthError('An error occurred. Please try again.');
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    setSuccessMessage('');
    
    // Validate passwords match
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }
    
    // Validate password strength
    if (resetPasswordData.newPassword.length < 8) {
      setAuthError('Password must be at least 8 characters long');
      return;
    }
    if (!/[A-Z]/.test(resetPasswordData.newPassword)) {
      setAuthError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(resetPasswordData.newPassword)) {
      setAuthError('Password must contain at least one lowercase letter');
      return;
    }
    if (!/\d/.test(resetPasswordData.newPassword)) {
      setAuthError('Password must contain at least one number');
      return;
    }
    
    setResetPasswordLoading(true);
    
    try {
      await axios.post(`${API}/reset-password`, {
        token: resetToken,
        new_password: resetPasswordData.newPassword,
        user_type: resetUserType
      });
      
      setSuccessMessage('Password has been reset successfully! Redirecting to login...');
      setResetPasswordData({ newPassword: '', confirmPassword: '' });
      
      // Redirect to home and show login modal after 3 seconds
      setTimeout(() => {
        setCurrentPage('home');
        setShowLoginModal(true);
        setSuccessMessage('');
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 3000);
    } catch (error) {
      console.error('Error resetting password:', error);
      if (error.response?.data?.detail) {
        setAuthError(error.response.data.detail);
      } else {
        setAuthError('An error occurred. Please try again.');
      }
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImageUploadError('');
    
    if (files.length < 4) {
      setImageUploadError('Please upload at least 4 images');
      return;
    }
    
    if (files.length > 10) {
      setImageUploadError('Maximum 10 images allowed');
      return;
    }
    
    setIsUploadingImages(true);
    
    Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            reject(new Error(`${file.name} is not a valid image file`));
            return;
          }
          
          // Validate file size (max 5MB)
          if (file.size > 5 * 1024 * 1024) {
            reject(new Error(`${file.name} is too large. Maximum 5MB allowed.`));
            return;
          }
          
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
          reader.readAsDataURL(file);
        });
      })
    )
    .then((imageDataUrls) => {
      setSellFormData({...sellFormData, images: imageDataUrls});
      setIsUploadingImages(false);
    })
    .catch((error) => {
      setImageUploadError(error.message);
      setIsUploadingImages(false);
    });
  };

  const removeImage = (indexToRemove) => {
    const updatedImages = sellFormData.images.filter((_, index) => index !== indexToRemove);
    setSellFormData({...sellFormData, images: updatedImages});
    
    if (updatedImages.length < 4) {
      setImageUploadError('Minimum 4 images required');
    } else {
      setImageUploadError('');
    }
  };

  const handleSellRequest = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    // Validate images
    if (sellFormData.images.length < 4) {
      setImageUploadError('Please upload at least 4 images');
      return;
    }
    
    if (sellFormData.images.length > 10) {
      setImageUploadError('Maximum 10 images allowed');
      return;
    }
    
    try {
      const submitData = {
        ...sellFormData,
        purchase_year: sellFormData.purchase_year ? parseInt(sellFormData.purchase_year) : null,
        estimated_value: sellFormData.estimated_value ? parseFloat(sellFormData.estimated_value) : null
      };
      
      await axios.post(`${API}/sell-requests`, submitData);
      
      setSuccessMessage('Thank you! We\'ve received your consignment request with images and will provide a quote within 24 hours.');
      
      // Clear success message after 8 seconds
      setTimeout(() => setSuccessMessage(''), 8000);
      
      // Reset form
      setSellFormData({
        name: '',
        email: '',
        phone: '',
        item_type: '',
        brand: '',
        model: '',
        description: '',
        condition: '',
        authenticity_cards: false,
        original_packaging: false,
        purchase_year: '',
        estimated_value: '',
        images: []
      });
      setImageUploadError('');
      
    } catch (error) {
      console.error('Error submitting sell request:', error);
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          setAuthError(error.response.data.detail.map(err => err.msg).join(', '));
        } else {
          setAuthError(error.response.data.detail);
        }
      } else {
        setAuthError('An error occurred while submitting your request. Please try again.');
      }
      setTimeout(() => setAuthError(''), 8000);
    }
  };

  const handlePurchase = async (productId) => {
    if (!user) {
      setShowSignupModal(true);
      return;
    }
    
    setIsProcessingPurchase(true);
    try {
      const response = await axios.post(`${API}/purchase/${productId}/${user.email}`);
      
      // Show success message
      setSuccessMessage(
        `Purchase successful! You earned ${formatCurrency(response.data.points_earned / 100, response.data.currency)} in wallet credits and saved ${response.data.co2_saved}L of CO₂!`
      );
      
      // Clear success message after 8 seconds
      setTimeout(() => setSuccessMessage(''), 8000);
      
      // Refresh products to update inventory
      await fetchProducts(selectedCategory);
      
      // Update user data with fresh data from server
      const userResponse = await axios.get(`${API}/users/${user.email}`);
      const updatedUser = userResponse.data;
      setUser(updatedUser);
      
      // Update localStorage with fresh user data
      localStorage.setItem('perfectCircleUser', JSON.stringify(updatedUser));
      
    } catch (error) {
      console.error('Error making purchase:', error);
      setAuthError('Error processing purchase. Please try again.');
      setTimeout(() => setAuthError(''), 5000);
    } finally {
      setIsProcessingPurchase(false);
    }
  };

  const handleSignOut = () => {
    setUser(null);
    localStorage.removeItem('perfectCircleUser');
    setCurrentPage('home');
    setSelectedCategory('all');
    setSuccessMessage('');
    setAuthError('');
    // Refresh products with default currency
    fetchProducts();
  };

  const handleUpdateUser = (updatedUser) => {
    setUser(updatedUser);
    // Update localStorage with updated user data
    localStorage.setItem('perfectCircleUser', JSON.stringify(updatedUser));
    // Refresh products with new currency
    fetchProducts(selectedCategory);
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return phoneNumber;
    }
  };

  const handlePhoneChange = (e) => {
    // Allow direct input on mobile - strip non-digits for storage
    const value = e.target.value;
    // Remove all non-digit characters for backend
    const digitsOnly = value.replace(/\D/g, '');
    setFormData({ ...formData, phone: digitsOnly });
  };

  // Show Rewards Page
  if (currentPage === 'rewards') {
    return (
      <RewardsPage 
        user={user} 
        onBack={goToHome}
        onSignOut={handleSignOut}
      />
    );
  }

  // Show Account Page
  if (currentPage === 'account') {
    return (
      <AccountPage 
        user={user} 
        onBack={goToHome}
        onSignOut={handleSignOut}
        onViewRewards={goToRewards}
        onUpdateUser={handleUpdateUser}
      />
    );
  }

  // Show Reset Password Page
  if (currentPage === 'reset-password') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-light tracking-wide text-black mb-2">Reset Password</h1>
            <p className="text-gray-600 text-sm">Enter your new password below.</p>
          </div>
          
          <form onSubmit={handleResetPassword} className="space-y-6">
            {authError && (
              <Alert variant="destructive">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
            {successMessage && (
              <Alert className="bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
              </Alert>
            )}
            
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative mt-1">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData({...resetPasswordData, newPassword: e.target.value})}
                  className="pr-10 w-full"
                  required
                  disabled={resetPasswordLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters with uppercase, lowercase, and a number.
              </p>
            </div>
            
            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                value={resetPasswordData.confirmPassword}
                onChange={(e) => setResetPasswordData({...resetPasswordData, confirmPassword: e.target.value})}
                className="mt-1 w-full"
                required
                disabled={resetPasswordLoading}
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-black hover:bg-gray-800 text-white py-3"
              disabled={resetPasswordLoading}
            >
              {resetPasswordLoading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
            
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setCurrentPage('home');
                  window.history.replaceState({}, document.title, window.location.pathname);
                }}
                className="text-sm text-gray-600 hover:text-black underline"
                disabled={resetPasswordLoading}
              >
                Back to Home
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Main App Routing
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            
            {/* Logo - Responsive */}
            <div className="flex items-center">
              <button onClick={goToHome} className="flex items-center space-x-2 sm:space-x-4 hover:opacity-80 transition-opacity">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-black rounded-full flex items-center justify-center">
                  <Leaf className="w-3 h-3 sm:w-5 sm:h-5 text-white" />
                </div>
                <h1 className="text-sm sm:text-2xl font-serif text-black tracking-wide">
                  PERFECT CIRCLE
                </h1>
              </button>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-12">
              <button onClick={() => {
                if (user) {
                  goToShop();
                } else {
                  setShowSignupModal(true);
                }
              }} className="text-gray-800 hover:text-black transition-colors text-sm tracking-wide uppercase font-medium">Shop</button>
              <button onClick={goToSellerRegistration} className="text-gray-800 hover:text-black transition-colors text-sm tracking-wide uppercase font-medium">Sell With Us</button>
              {user && (
                <button 
                  onClick={goToRewards}
                  className="text-gray-800 hover:text-black transition-colors text-sm tracking-wide uppercase font-medium"
                >
                  Rewards
                </button>
              )}
            </nav>

            {/* Right Side - Mobile Responsive */}
            <div className="flex items-center space-x-1 sm:space-x-4">
              
              {/* Cart - Always visible but smaller on mobile */}
              <div className="relative">
                <Button 
                  variant="ghost" 
                  onClick={() => setShowCart(true)}
                  className="flex items-center space-x-1 bg-gray-50 px-2 sm:px-4 py-1 sm:py-2 rounded-sm hover:bg-gray-100"
                >
                  <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" />
                  <span className="text-xs sm:text-sm font-medium text-gray-900 hidden sm:inline">
                    Cart ({getCartItemCount()})
                  </span>
                  <span className="text-xs font-medium text-gray-900 sm:hidden">
                    ({getCartItemCount()})
                  </span>
                </Button>
              </div>
              
              {user ? (
                <>
                  {/* Wallet - Hidden on small mobile, visible on sm+ */}
                  <div className="hidden sm:flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-sm">
                    <Wallet className="w-3 h-3 text-gray-700" />
                    <span className="text-xs font-medium text-gray-900">
                      {formatCurrency(user.wallet_points / 100, user.currency)}
                    </span>
                  </div>
                  
                  {/* Wishlist - Compact on mobile */}
                  <div className="flex items-center space-x-1 bg-gray-50 px-2 sm:px-3 py-1 sm:py-2 rounded-sm">
                    <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" />
                    <span className="text-xs sm:text-sm font-medium text-gray-900">
                      <span className="hidden sm:inline">Wishlist </span>{wishlist.length}
                    </span>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    onClick={goToAccount}
                    className="flex items-center space-x-1 text-gray-700 hover:text-black transition-colors px-2 sm:px-3 py-1 sm:py-2"
                  >
                    <User className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm hidden sm:inline">Account</span>
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center space-x-1 bg-gray-50 px-2 sm:px-3 py-1 sm:py-2 rounded-sm">
                    <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" />
                    <span className="text-xs sm:text-sm font-medium text-gray-900">
                      <span className="hidden sm:inline">Wishlist </span>{wishlist.length}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-3">
                    <Button 
                      variant="ghost" 
                      onClick={() => setShowLoginModal(true)}
                      className="text-xs sm:text-sm text-gray-700 hover:text-black transition-colors px-2 py-1"
                    >
                      Sign In
                    </Button>
                    <Button 
                      onClick={() => setShowSignupModal(true)}
                      className="bg-black text-white hover:bg-gray-800 px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm"
                    >
                      Register
                    </Button>
                  </div>
                </>
              )}
              <Button 
                variant="ghost" 
                size="sm"
                className="md:hidden text-black"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Success Message Banner */}
      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mx-6 lg:mx-8 mt-6">
          <div className="flex">
            <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-green-700 text-sm font-medium">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message Banner */}
      {authError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-6 lg:mx-8 mt-6">
          <div className="flex">
            <X className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-red-700 text-sm font-medium">{authError}</p>
          </div>
        </div>
      )}

      {/* Page Content */}
      {currentPage === 'home' && (
        <HomePage 
          user={user}
          formatCurrency={formatCurrency}
          goToShop={goToShop}
          goToSellerRegistration={goToSellerRegistration}
          setShowSignupModal={setShowSignupModal}
        />
      )}
      
      {currentPage === 'shop' && (
        <ShopPage 
          products={products}
          shopCategory={shopCategory}
          selectedCategory={selectedCategory}
          user={user}
          formatCurrency={formatCurrency}
          handleCategoryChange={handleCategoryChange}
          handlePurchase={handlePurchase}
          isProcessingPurchase={isProcessingPurchase}
          addToWishlist={addToWishlist}
          isInWishlist={isInWishlist}
          setShopCategory={setShopCategory}
          addToCart={addToCart}
          setShowSignupModal={setShowSignupModal}
        />
      )}
      
      {currentPage === 'seller-registration' && (
        <SellerRegistrationPage API={API} axios={axios} />
      )}
      
      {currentPage === 'account' && (
        <AccountPage 
          user={user} 
          onBack={goToHome}
          onSignOut={handleSignOut}
          onViewRewards={goToRewards}
          onUpdateUser={handleUpdateUser}
        />
      )}
      
      {currentPage === 'rewards' && (
        <RewardsPage 
          user={user} 
          onBack={goToHome}
          onSignOut={handleSignOut}
        />
      )}

      {/* Hero Section */}
      <section className="relative py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-6xl md:text-7xl font-serif text-black mb-8 leading-tight">
            Circular Luxury
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            Authenticated pre-owned designer pieces that reduce environmental impact. 
            Every purchase saves water, reduces CO₂ emissions, and rewards your sustainable choices.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              size="lg" 
              onClick={() => {
                if (user) {
                  goToShop();
                } else {
                  setShowSignupModal(true);
                }
              }}
              className="bg-black hover:bg-gray-900 text-white px-12 py-4 text-sm tracking-wide uppercase font-medium"
            >
              Shop Collection
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              onClick={() => {
                if (user) {
                  goToSellerRegistration();
                } else {
                  setShowSignupModal(true);
                }
              }}
              className="border-black text-black hover:bg-black hover:text-white px-12 py-4 text-sm tracking-wide uppercase font-medium"
            >
              Consign With Us
            </Button>
          </div>
        </div>
      </section>

      {/* Login/Signup Modals */}
      <Dialog open={showSignupModal} onOpenChange={setShowSignupModal}>
        <DialogContent className="w-[95vw] max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-light tracking-wide text-black text-center">
              Create Account
            </DialogTitle>
            <DialogDescription className="text-gray-500 text-sm text-center">
              Join Perfect Circle and get 25% off your first purchase.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSignup} className="space-y-4">
            {authError && (
              <Alert variant="destructive">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1 w-full"
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1 w-full"
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <div className="flex gap-2 mt-1">
                <Select value={formData.countryCode || '+44'} onValueChange={(value) => setFormData({...formData, countryCode: value})}>
                  <SelectTrigger className="w-20 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="+44">🇬🇧 +44</SelectItem>
                    <SelectItem value="+1">🇺🇸 +1</SelectItem>
                    <SelectItem value="+33">🇫🇷 +33</SelectItem>
                    <SelectItem value="+49">🇩🇪 +49</SelectItem>
                    <SelectItem value="+39">🇮🇹 +39</SelectItem>
                    <SelectItem value="+34">🇪🇸 +34</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="7700123456"
                  className="flex-1 min-w-0"
                  required
                  autoComplete="tel"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="currency">Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.symbol} {currency.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="pr-10 w-full"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                8+ characters with uppercase, lowercase, and number
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-black hover:bg-gray-800 text-white">
                Create Account
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowSignupModal(false)}
                className="sm:w-auto"
              >
                Later
              </Button>
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowSignupModal(false);
                  setShowLoginModal(true);
                  setAuthError('');
                }}
                className="text-sm text-gray-600 hover:text-black underline"
              >
                Already have an account? Sign In
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="w-[95vw] max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-light tracking-wide text-black text-center">
              Welcome Back
            </DialogTitle>
            <DialogDescription className="text-gray-500 text-sm text-center">
              Sign in to your Perfect Circle account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            {authError && (
              <Alert variant="destructive">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label htmlFor="login-email">Email Address</Label>
              <Input
                id="login-email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                className="mt-1 w-full"
                required
              />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  className="pr-10 w-full"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-black hover:bg-gray-800 text-white">
                Sign In
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowLoginModal(false)}
                className="sm:w-auto"
              >
                Cancel
              </Button>
            </div>
            <div className="flex flex-col space-y-2 text-center">
              <button
                type="button"
                onClick={() => {
                  setShowLoginModal(false);
                  setShowForgotPasswordModal(true);
                  setAuthError('');
                }}
                className="text-sm text-gray-600 hover:text-black underline"
              >
                Forgot Password?
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLoginModal(false);
                  setShowSignupModal(true);
                  setAuthError('');
                }}
                className="text-sm text-gray-600 hover:text-black underline"
              >
                Don't have an account? Create Account
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPasswordModal} onOpenChange={setShowForgotPasswordModal}>
        <DialogContent className="w-[95vw] max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-light tracking-wide text-black text-center">
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-gray-500 text-sm text-center">
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            {authError && (
              <Alert variant="destructive">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
            {successMessage && (
              <Alert className="bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label htmlFor="forgot-email">Email Address</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                className="mt-1 w-full"
                required
                disabled={forgotPasswordLoading}
                placeholder="your@email.com"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                type="submit" 
                className="flex-1 bg-black hover:bg-gray-800 text-white"
                disabled={forgotPasswordLoading}
              >
                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowForgotPasswordModal(false);
                  setForgotPasswordEmail('');
                  setAuthError('');
                }}
                className="sm:w-auto"
                disabled={forgotPasswordLoading}
              >
                Cancel
              </Button>
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPasswordModal(false);
                  setShowLoginModal(true);
                  setAuthError('');
                  setForgotPasswordEmail('');
                }}
                className="text-sm text-gray-600 hover:text-black underline"
                disabled={forgotPasswordLoading}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cart Modal */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="w-[calc(100vw-32px)] max-w-lg bg-white max-h-[90vh] overflow-y-auto m-4 rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-light tracking-wide text-black">Shopping Cart</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {cart.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Your cart is empty</p>
            ) : (
              <>
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4 py-4 border-b">
                    <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded" />
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{item.name}</h4>
                      <p className="text-gray-500 text-xs">{item.brand}</p>
                      <p className="text-black font-medium text-sm">{formatCurrency(item.price, user?.currency || 'GBP')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 p-0"
                      >
                        -
                      </Button>
                      <span className="text-sm font-medium px-2">{item.quantity}</span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 p-0"
                      >
                        +
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeFromCart(item.id)}
                        className="w-8 h-8 p-0 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-medium">Total: {formatCurrency(getCartTotal(), user?.currency || 'GBP')}</span>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowCart(false)}
                      className="flex-1"
                    >
                      Continue Shopping
                    </Button>
                    <Button 
                      onClick={proceedToCheckout}
                      className="flex-1 bg-black hover:bg-gray-800 text-white"
                    >
                      Checkout
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="w-[calc(100vw-32px)] max-w-2xl bg-white max-h-[90vh] overflow-y-auto m-4 rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-light tracking-wide text-black">Checkout</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Checkout Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-700 tracking-wide uppercase">First Name</Label>
                  <Input
                    value={checkoutData.firstName}
                    onChange={(e) => setCheckoutData({...checkoutData, firstName: e.target.value})}
                    className="bg-gray-50 border-gray-200 h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-700 tracking-wide uppercase">Last Name</Label>
                  <Input
                    value={checkoutData.lastName}
                    onChange={(e) => setCheckoutData({...checkoutData, lastName: e.target.value})}
                    className="bg-gray-50 border-gray-200 h-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-700 tracking-wide uppercase">Email</Label>
                <Input
                  type="email"
                  value={checkoutData.email}
                  onChange={(e) => setCheckoutData({...checkoutData, email: e.target.value})}
                  className="bg-gray-50 border-gray-200 h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-700 tracking-wide uppercase">Address</Label>
                <Input
                  value={checkoutData.address}
                  onChange={(e) => setCheckoutData({...checkoutData, address: e.target.value})}
                  className="bg-gray-50 border-gray-200 h-9 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-700 tracking-wide uppercase">City</Label>
                  <Input
                    value={checkoutData.city}
                    onChange={(e) => setCheckoutData({...checkoutData, city: e.target.value})}
                    className="bg-gray-50 border-gray-200 h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-700 tracking-wide uppercase">Postal Code</Label>
                  <Input
                    value={checkoutData.postalCode}
                    onChange={(e) => setCheckoutData({...checkoutData, postalCode: e.target.value})}
                    className="bg-gray-50 border-gray-200 h-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-700 tracking-wide uppercase">Delivery Method</Label>
                <Select value={checkoutData.deliveryMethod} onValueChange={(value) => setCheckoutData({...checkoutData, deliveryMethod: value})}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Delivery (3-5 days) - Free</SelectItem>
                    <SelectItem value="express">Express Delivery (1-2 days) - £9.99</SelectItem>
                    <SelectItem value="next">Next Day Delivery - £19.99</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-700 tracking-wide uppercase">Payment Method</Label>
                <Select value={checkoutData.paymentMethod} onValueChange={(value) => setCheckoutData({...checkoutData, paymentMethod: value})}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="card">Credit/Debit Card</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="apple">Apple Pay</SelectItem>
                    <SelectItem value="google">Google Pay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Order Summary */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-medium text-lg mb-4">Order Summary</h3>
              <div className="space-y-3 mb-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span>{item.name} (x{item.quantity})</span>
                    <span>{formatCurrency(item.price * item.quantity, user?.currency || 'GBP')}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(getCartTotal(), user?.currency || 'GBP')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Delivery</span>
                  <span>
                    {checkoutData.deliveryMethod === 'standard' ? 'Free' : 
                     checkoutData.deliveryMethod === 'express' ? '£9.99' : '£19.99'}
                  </span>
                </div>
                <div className="flex justify-between font-medium text-lg border-t pt-2">
                  <span>Total</span>
                  <span>
                    {formatCurrency(
                      getCartTotal() + 
                      (checkoutData.deliveryMethod === 'standard' ? 0 : 
                       checkoutData.deliveryMethod === 'express' ? 9.99 : 19.99), 
                      user?.currency || 'GBP'
                    )}
                  </span>
                </div>
              </div>
              <Button 
                className="w-full mt-6 bg-black hover:bg-gray-800 text-white py-3"
                onClick={() => {
                  setSuccessMessage('Order placed successfully! You will receive a confirmation email shortly.');
                  setCart([]);
                  setShowCheckout(false);
                  setTimeout(() => setSuccessMessage(''), 5000);
                }}
              >
                Place Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-black text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            
            {/* Company Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium tracking-wide uppercase">Perfect Circle</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <a href="#" className="block hover:text-white transition-colors">About Us</a>
                <a href="#" className="block hover:text-white transition-colors">Our Story</a>
                <a href="#" className="block hover:text-white transition-colors">Sustainability</a>
                <a href="#" className="block hover:text-white transition-colors">Careers</a>
                <a href="#" className="block hover:text-white transition-colors">Press</a>
                <a href="#" className="block hover:text-white transition-colors">Affiliate Program</a>
              </div>
            </div>

            {/* Customer Service */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium tracking-wide uppercase">Customer Service</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <a href="#" className="block hover:text-white transition-colors">Help Center</a>
                <a href="#" className="block hover:text-white transition-colors">Contact Us</a>
                <a href="#" className="block hover:text-white transition-colors">Shipping & Delivery</a>
                <a href="#" className="block hover:text-white transition-colors">Returns & Refunds</a>
                <a href="#" className="block hover:text-white transition-colors">Size Guide</a>
                <a href="#" className="block hover:text-white transition-colors">Authentication</a>
              </div>
            </div>

            {/* Legal & Privacy */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium tracking-wide uppercase">Legal</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <a href="#" className="block hover:text-white transition-colors">Terms of Use</a>
                <a href="#" className="block hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="block hover:text-white transition-colors">Cookie Policy</a>
                <a href="#" className="block hover:text-white transition-colors">Consumer Rights</a>
                <a href="#" className="block hover:text-white transition-colors">Seller Agreement</a>
              </div>
            </div>

            {/* Newsletter & Social */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium tracking-wide uppercase">Stay Connected</h3>
              <p className="text-sm text-gray-300 leading-relaxed">
                Subscribe to receive updates on new arrivals, exclusive offers, and sustainability initiatives.
              </p>
              
              {/* Newsletter Signup */}
              <div className="space-y-3">
                <div className="flex">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    className="bg-gray-800 border-gray-700 text-white placeholder-gray-400 h-10 text-sm flex-1 rounded-r-none focus:border-white"
                  />
                  <Button className="bg-white text-black hover:bg-gray-200 h-10 px-6 text-sm font-medium rounded-l-none">
                    Subscribe
                  </Button>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="space-y-3">
                <p className="text-sm text-gray-400 uppercase tracking-wide">Follow Us</p>
                <div className="flex space-x-4">
                  <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                    <span className="text-xs">f</span>
                  </a>
                  <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                    <span className="text-xs">ig</span>
                  </a>
                  <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                    <span className="text-xs">tw</span>
                  </a>
                  <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors">
                    <span className="text-xs">pin</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800 pt-8">
            
            {/* Payment Methods & Security */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">Payment Methods</h4>
                <div className="flex space-x-3">
                  <div className="w-10 h-6 bg-gray-700 rounded text-xs flex items-center justify-center text-gray-300">VISA</div>
                  <div className="w-10 h-6 bg-gray-700 rounded text-xs flex items-center justify-center text-gray-300">MC</div>
                  <div className="w-10 h-6 bg-gray-700 rounded text-xs flex items-center justify-center text-gray-300">AMEX</div>
                  <div className="w-10 h-6 bg-gray-700 rounded text-xs flex items-center justify-center text-gray-300">PP</div>
                  <div className="w-10 h-6 bg-gray-700 rounded text-xs flex items-center justify-center text-gray-300">AP</div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">Security & Trust</h4>
                <div className="flex space-x-3">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-gray-300">SSL Secured</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-gray-300">Expert Authentication</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0 text-sm text-gray-400">
              <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6">
                <p>&copy; 2025 Perfect Circle. All rights reserved.</p>
                <div className="flex items-center space-x-2">
                  <Leaf className="w-4 h-4 text-green-400" />
                  <span>Carbon Neutral Shipping</span>
                </div>
              </div>
              
              {/* Language & Currency Selector */}
              <div className="flex items-center space-x-4">
                <Select defaultValue="en-gb">
                  <SelectTrigger className="w-32 bg-gray-800 border-gray-700 text-white h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="en-gb">🇬🇧 English (UK)</SelectItem>
                    <SelectItem value="en-us">🇺🇸 English (US)</SelectItem>
                    <SelectItem value="fr">🇫🇷 Français</SelectItem>
                    <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select defaultValue="gbp">
                  <SelectTrigger className="w-20 bg-gray-800 border-gray-700 text-white h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="gbp">GBP £</SelectItem>
                    <SelectItem value="usd">USD $</SelectItem>
                    <SelectItem value="eur">EUR €</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;